import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { submitQuiz } from "@/lib/api";
import { trackQuizComplete } from "@/lib/tiktok";
import { trackGAQuizComplete } from "@/lib/ga";
import { usePageMeta } from "@/lib/usePageMeta";

const questions = [
  { key: "home", title: "Where will your dog live?", options: [
    { v: "apartment", l: "Apartment", d: "No yard, shared building" },
    { v: "house_no_yard", l: "House, no yard", d: "Standalone but no outdoor space" },
    { v: "house_yard", l: "House with yard", d: "Room to run" },
  ]},
  { key: "weight_limit", title: "Any weight limit from your building or HOA?", options: [
    { v: "any", l: "No limit", d: "Any size is allowed" },
    { v: "10", l: "Under 10 kg", d: "Small breeds only (~under 22 lbs)" },
    { v: "15", l: "Under 15 kg", d: "Small to medium (~under 33 lbs)" },
    { v: "20", l: "Under 20 kg", d: "No large breeds (~under 44 lbs)" },
    { v: "25", l: "Under 25 kg", d: "No giant breeds (~under 55 lbs)" },
  ]},
  { key: "breed_restrictions", title: "Are certain breeds restricted where you live?", options: [
    { v: "none", l: "No restrictions", d: "All breeds allowed" },
    { v: "some_restricted", l: "Yes, some restricted", d: "BSL zones, insurance limits, or HOA rules" },
    { v: "unsure", l: "Not sure", d: "I'll assume no restrictions for now" },
  ]},
  { key: "activity", title: "How active are you?", options: [
    { v: "low", l: "Low", d: "A slow walk is plenty" },
    { v: "medium", l: "Medium", d: "Daily walks, weekend hikes" },
    { v: "high", l: "High", d: "Running, hiking, dog sports" },
  ]},
  { key: "hours_alone", title: "How long will the dog be alone on a typical day?", options: [
    { v: "0-3", l: "0-3 hours", d: "Home most of the day" },
    { v: "4-6", l: "4-6 hours", d: "Part-day out" },
    { v: "7-plus", l: "7+ hours", d: "Full workday away" },
  ]},
  { key: "travel_frequency", title: "How often do you travel overnight?", options: [
    { v: "rare", l: "Rarely", d: "A few times a year at most" },
    { v: "monthly", l: "Monthly", d: "1-2 short trips a month" },
    { v: "frequent", l: "Frequently", d: "Weekly or long stretches away" },
  ]},
  { key: "time_horizon", title: "Ready for a 10-15+ year commitment?", options: [
    { v: "stable", l: "Yes, my life feels stable", d: "Puppy or any age fine" },
    { v: "uncertain", l: "Uncertain — big changes ahead", d: "Career, moves, kids or family shifts on the horizon" },
  ]},
  { key: "experience", title: "Owned a dog before?", options: [
    { v: "first", l: "First-timer", d: "Brand new to this" },
    { v: "some", l: "Some experience", d: "Grew up with one or one previous" },
    { v: "experienced", l: "Experienced", d: "Trained multiple dogs" },
  ]},
  { key: "kids", title: "Kids at home?", options: [
    { v: "yes", l: "Yes", d: "Or often visiting" },
    { v: "no", l: "No", d: "Adults only household" },
  ]},
  { key: "other_pets", title: "Other pets?", options: [
    { v: "yes", l: "Yes", d: "Existing cat, dog, or small pet" },
    { v: "no", l: "No", d: "This will be the only one" },
  ]},
  { key: "allergies", title: "Anyone with dog allergies?", options: [
    { v: "yes", l: "Yes", d: "Need a low-allergen breed" },
    { v: "no", l: "No", d: "Any coat is fine" },
  ]},
  { key: "grooming_tolerance", title: "How much grooming are you up for?", options: [
    { v: "low", l: "Minimal", d: "Occasional brushing at most" },
    { v: "medium", l: "Some", d: "Weekly brushing, monthly groom" },
    { v: "high", l: "A lot", d: "Daily brushing + regular pro grooms" },
  ]},
  { key: "barking_tolerance", title: "Barking tolerance?", options: [
    { v: "low", l: "Low", d: "Neighbors, apartment life" },
    { v: "medium", l: "Medium", d: "Some barking is fine" },
    { v: "high", l: "High", d: "Bark all you want, buddy" },
  ]},
  { key: "size_pref", title: "Preferred size?", options: [
    { v: "small", l: "Small", d: "Under 25 lbs" },
    { v: "medium", l: "Medium", d: "25-55 lbs" },
    { v: "large", l: "Large", d: "55+ lbs" },
    { v: "any", l: "No preference", d: "Show me anything" },
  ]},
  { key: "budget", title: "Monthly budget?", options: [
    { v: "tight", l: "Tight ($100-150)", d: "Small breeds fit best" },
    { v: "moderate", l: "Moderate ($150-250)", d: "Most breeds work" },
    { v: "flexible", l: "Flexible ($250+)", d: "Any breed goes" },
  ]},
];

export default function Quiz() {
  usePageMeta("Dog Breed Quiz — Which Breed Fits Your Lifestyle? | Fetchright", "Answer 15 quick questions about your home, schedule and budget to find your best-matched dog breeds. Free, takes 2 minutes.");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const q = questions[step];
  const progress = ((step + 1) / questions.length) * 100;

  const pick = async (val) => {
    const next = { ...answers, [q.key]: val };
    setAnswers(next);
    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 180);
    } else {
      setLoading(true);
      try {
        const res = await submitQuiz(next);
        sessionStorage.setItem("pm_results", JSON.stringify(res));
        sessionStorage.setItem("pm_answers", JSON.stringify(next));
        trackQuizComplete();
        trackGAQuizComplete();
        nav("/results");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
          <span data-testid="quiz-progress-text">Question {step + 1} of {questions.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <motion.div
            data-testid="quiz-progress-bar"
            className="h-full bg-primary rounded-full"
            animate={{ width: `${progress}%` }}
            transition={{ type: "spring", stiffness: 60 }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          <h2 data-testid="quiz-question" className="font-display font-bold text-3xl md:text-4xl text-accent leading-tight">
            {q.title}
          </h2>

          <div className={`mt-8 grid gap-4 ${q.options.length > 3 ? "sm:grid-cols-2" : "sm:grid-cols-1"}`}>
            {q.options.map((opt) => {
              const active = answers[q.key] === opt.v;
              return (
                <button
                  key={opt.v}
                  data-testid={`quiz-option-${q.key}-${opt.v}`}
                  onClick={() => pick(opt.v)}
                  className={`text-left rounded-3xl border-2 p-6 transition-colors duration-200 group ${
                    active
                      ? "border-primary bg-primary/5"
                      : "border-border/70 bg-card hover:border-primary/60"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-bold text-xl text-accent">
                        {opt.l}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {opt.d}
                      </div>
                    </div>
                    <span
                      className={`shrink-0 w-8 h-8 rounded-full grid place-items-center transition-colors ${
                        active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                      }`}
                    >
                      <Check size={16} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-10 flex justify-between items-center">
        <button
          data-testid="quiz-back-btn"
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="pill-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-accent disabled:opacity-40"
        >
          <ArrowLeft size={16} /> Back
        </button>
        {loading && (
          <span data-testid="quiz-loading" className="text-sm text-muted-foreground animate-pulse">
            Finding your matches…
          </span>
        )}
      </div>
    </div>
  );
}
